import React, {useState, useEffect} from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Snackbar,
  DialogContentText,
  Switch,
  FormControlLabel,
  Grid,
  Card,
  CardContent,
  InputAdornment,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import {useAuth} from "../../contexts/AuthContext";
import {fetchUsers, createUser, updateUser, deleteUser, User, UserRole, roleLabels, CreateUserDto, UpdateUserDto} from "../../api/user";
import {fetchTenants, Tenant} from "../../api/tenant";
import {fetchFarms, Farm} from "../../api/farm";
import {useTranslation} from "react-i18next";

const UserListPage: React.FC = () => {
  const {user: currentUser} = useAuth();
  const {t} = useTranslation();
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [tenantMap, setTenantMap] = useState<Record<string, string>>({});
  const [farms, setFarms] = useState<Farm[]>([]);
  const [farmMap, setFarmMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);

  // Filter states
  const [filters, setFilters] = useState({
    name: "",
    email: "",
    role: "",
    tenantId: "",
    farmId: "",
    status: "",
  });

  // Dialog states
  const [openCreateDialog, setOpenCreateDialog] = useState<boolean>(false);
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Form states
  const [formData, setFormData] = useState<Partial<CreateUserDto>>({
    name: "",
    email: "",
    password: "Password@123", // Default password
    role: UserRole.EMPLOYEE,
    tenantId: currentUser?.tenantId || "", // Include current user's tenantId
    farmId: "", // Include farmId for farm-related roles
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Snackbar states
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
  }>({open: false, message: "", severity: "info"});

  // Fetch users and tenants on component mount
  useEffect(() => {
    loadData();
  }, []);

  // Apply filters whenever users or filters change
  useEffect(() => {
    applyFilters();
  }, [users, filters]);

  const applyFilters = () => {
    let filtered = [...users];

    // Filter by name
    if (filters.name) {
      filtered = filtered.filter((user) => user.name.toLowerCase().includes(filters.name.toLowerCase()));
    }

    // Filter by email
    if (filters.email) {
      filtered = filtered.filter((user) => user.email.toLowerCase().includes(filters.email.toLowerCase()));
    }

    // Filter by role
    if (filters.role) {
      filtered = filtered.filter((user) => user.role === filters.role);
    }

    // Filter by tenant
    if (filters.tenantId) {
      filtered = filtered.filter((user) => user.tenantId === filters.tenantId);
    }

    // Filter by farm
    if (filters.farmId) {
      filtered = filtered.filter((user) => user.farmId === filters.farmId);
    }

    // Filter by status
    if (filters.status) {
      const isActive = filters.status === "active";
      filtered = filtered.filter((user) => user.isActive === isActive);
    }

    setFilteredUsers(filtered);
    setPage(0); // Reset to first page when filters change
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      name: "",
      email: "",
      role: "",
      tenantId: "",
      farmId: "",
      status: "",
    });
  };

  const hasActiveFilters = () => {
    return Object.values(filters).some((value) => value !== "");
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersData, tenantsData, farmsData] = await Promise.all([fetchUsers(), fetchTenants(), fetchFarms()]);

      setUsers(usersData);
      setTenants(tenantsData);
      setFarms(farmsData);

      // Create tenant ID to name mapping
      const tenantMapping: Record<string, string> = {};
      tenantsData.forEach((tenant) => {
        tenantMapping[tenant.id] = tenant.name;
      });
      setTenantMap(tenantMapping);

      // Create farm ID to name mapping
      const farmMapping: Record<string, string> = {};
      farmsData.forEach((farm) => {
        farmMapping[farm.id] = farm.name;
      });
      setFarmMap(farmMapping);
    } catch (error) {
      console.error("Error fetching data:", error);
      showSnackbar("Failed to load data", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await fetchUsers();
      setUsers(data);
    } catch (error) {
      console.error("Error fetching users:", error);
      showSnackbar("Failed to load users", "error");
    } finally {
      setLoading(false);
    }
  };

  // Get tenant name for a user
  const getTenantName = (tenantId: string | null): string => {
    if (!tenantId) return "No Tenant";
    return tenantMap[tenantId] || "Unknown Tenant";
  };

  // Get farm name for a user
  const getFarmName = (farmId: string | null): string => {
    if (!farmId) return "No Farm";
    return farmMap[farmId] || "Unknown Farm";
  };

  // Handle pagination
  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Dialog handlers
  const handleOpenCreateDialog = async () => {
    setErrors({});
    setFormData({
      name: "",
      email: "",
      password: "Password@123",
      role: UserRole.EMPLOYEE,
      tenantId: canSelectTenant() ? "" : (currentUser?.tenantId as string) || "",
      farmId: "",
    });
    try {
      setLoading(true);
      const farmsData = await fetchFarms();
      setFarms(farmsData);
    } catch (error) {
      console.error("Error fetching farms:", error);
      showSnackbar("Failed to load farms", "error");
    } finally {
      setLoading(false);
      setOpenCreateDialog(true);
    }
  };

  const handleOpenEditDialog = (user: User) => {
    setSelectedUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      role: user.role,
      farmId: user.farmId || "",
    });
    setErrors({});
    setOpenEditDialog(true);
  };

  const handleOpenDeleteDialog = (user: User) => {
    setSelectedUser(user);
    setOpenDeleteDialog(true);
  };

  const handleCloseDialogs = () => {
    setOpenCreateDialog(false);
    setOpenEditDialog(false);
    setOpenDeleteDialog(false);
    setSelectedUser(null);
  };

  // Form handlers
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {name, value} = e.target;

    // If tenant is being changed, clear the farm selection
    if (name === "tenantId") {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        farmId: "", // Clear farm selection when tenant changes
      }));
    } else if (name === "role") {
      // If role is being changed, clear farm selection if the new role doesn't require it
      const newFormData: Partial<CreateUserDto> = {
        ...formData,
        role: value as UserRole,
      };
      if (value && !isFarmSelectionRequired(value as UserRole)) {
        newFormData.farmId = "";
      }
      // For SUPER_ADMIN, clear tenantId as well
      if (value === UserRole.SUPER_ADMIN) {
        newFormData.tenantId = "";
      }
      setFormData(newFormData);
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    // Clear error when user types
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name?.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email?.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    if (openCreateDialog && !formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password && formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    // Validate tenant selection for roles that require it (but not for SUPER_ADMIN)
    if (formData.role && isTenantSelectionRequired(formData.role) && canSelectTenant() && !formData.tenantId) {
      newErrors.tenantId = "Tenant is required for this role";
    }

    // Validate farm selection for farm-related roles
    if (formData.role && isFarmSelectionRequired(formData.role) && !formData.farmId) {
      newErrors.farmId = "Farm is required for this role";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // CRUD operations
  const handleCreateUser = async () => {
    if (!validateForm()) return;

    try {
      // Prepare the data, excluding farmId and tenantId if they're empty
      const createData = {...formData};
      if (!createData.farmId || createData.farmId.trim() === "") {
        delete createData.farmId;
      }

      // For SUPER_ADMIN, exclude tenantId if it's empty
      if (createData.role === UserRole.SUPER_ADMIN && (!createData.tenantId || createData.tenantId.trim() === "")) {
        delete createData.tenantId;
      }

      await createUser(createData as CreateUserDto);
      showSnackbar("User created successfully", "success");
      handleCloseDialogs();
      loadData();
    } catch (error: any) {
      console.error("Error creating user:", error);
      const errorMessage = error.response?.data?.message || "Failed to create user";
      showSnackbar(errorMessage, "error");
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser || !validateForm()) return;

    try {
      // Don't send password if it's the default one or not changed
      const {password, ...updateData} = formData;

      // Exclude farmId if it's empty
      if (!updateData.farmId || updateData.farmId.trim() === "") {
        delete updateData.farmId;
      }

      const updatePayload: UpdateUserDto = {
        ...updateData,
        ...(password !== "Password@123" && {password}), // Only include password if changed
      };

      await updateUser(selectedUser.id, updatePayload);
      showSnackbar("User updated successfully", "success");
      handleCloseDialogs();
      loadData();
    } catch (error: any) {
      console.error("Error updating user:", error);
      const errorMessage = error.response?.data?.message || "Failed to update user";
      showSnackbar(errorMessage, "error");
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      await deleteUser(selectedUser.id);
      showSnackbar("User deleted successfully", "success");
      handleCloseDialogs();
      loadData();
    } catch (error: any) {
      console.error("Error deleting user:", error);
      const errorMessage = error.response?.data?.message || "Failed to delete user";
      showSnackbar(errorMessage, "error");
    }
  };

  const handleToggleStatus = async (user: User) => {
    try {
      await updateUser(user.id, {isActive: !user.isActive});
      showSnackbar(`User ${user.isActive ? "deactivated" : "activated"} successfully`, "success");
      loadData();
    } catch (error: any) {
      console.error("Error toggling user status:", error);
      const errorMessage = error.response?.data?.message || "Failed to update user status";
      showSnackbar(errorMessage, "error");
    }
  };

  // Helper functions
  const showSnackbar = (message: string, severity: "success" | "error" | "info" | "warning") => {
    setSnackbar({open: true, message, severity});
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({...prev, open: false}));
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Get user role options based on current user's role
  const getUserRoleOptions = () => {
    const options = [
      {value: UserRole.EMPLOYEE, label: roleLabels[UserRole.EMPLOYEE]},
      {value: UserRole.SUPERVISOR, label: roleLabels[UserRole.SUPERVISOR]},
    ];

    // Only SUPER_ADMIN can create other SUPER_ADMIN or TENANT_ADMIN
    if (currentUser?.role === UserRole.SUPER_ADMIN) {
      options.unshift(
        {value: UserRole.FARMER_ADMIN, label: roleLabels[UserRole.FARMER_ADMIN]},
        {value: UserRole.TENANT_ADMIN, label: roleLabels[UserRole.TENANT_ADMIN]},
        {value: UserRole.SUPER_ADMIN, label: roleLabels[UserRole.SUPER_ADMIN]}
      );
    } else if (currentUser?.role === UserRole.TENANT_ADMIN) {
      // TENANT_ADMIN can create FARMER_ADMIN, SUPERVISOR and EMPLOYEE
      options.unshift({value: UserRole.FARMER_ADMIN, label: roleLabels[UserRole.FARMER_ADMIN]});
    } else if (currentUser?.role === UserRole.FARMER_ADMIN) {
      // FARMER_ADMIN can create SUPERVISOR and EMPLOYEE
      options.unshift({value: UserRole.SUPERVISOR, label: roleLabels[UserRole.SUPERVISOR]});
    }

    return options;
  };

  // Check if current user can edit/delete other users
  const canEditUser = (targetUser: User) => {
    if (!currentUser) return false;

    // SUPER_ADMIN can edit anyone
    if (currentUser.role === UserRole.SUPER_ADMIN) {
      return true;
    }

    // TENANT_ADMIN can edit anyone except SUPER_ADMIN
    if (currentUser.role === UserRole.TENANT_ADMIN) {
      return targetUser.role !== UserRole.SUPER_ADMIN;
    }

    // FARMER_ADMIN can edit anyone except SUPER_ADMIN and TENANT_ADMIN
    if (currentUser.role === UserRole.FARMER_ADMIN) {
      return targetUser.role !== UserRole.SUPER_ADMIN && targetUser.role !== UserRole.TENANT_ADMIN;
    }

    return false;
  };

  const canDeleteUser = (targetUser: User) => {
    if (!currentUser) return false;

    // SUPER_ADMIN can delete anyone except themselves
    if (currentUser.role === UserRole.SUPER_ADMIN) {
      return targetUser.id !== currentUser.id;
    }

    // TENANT_ADMIN can delete non-SUPER_ADMIN users
    if (currentUser.role === UserRole.TENANT_ADMIN) {
      return targetUser.role !== UserRole.SUPER_ADMIN;
    }

    // FARMER_ADMIN can delete non-SUPER_ADMIN and non-TENANT_ADMIN users
    if (currentUser.role === UserRole.FARMER_ADMIN) {
      return targetUser.role !== UserRole.SUPER_ADMIN && targetUser.role !== UserRole.TENANT_ADMIN;
    }

    return false;
  };

  // Check if current user can select tenant (only Super Admin)
  const canSelectTenant = (): boolean => {
    return currentUser?.role === UserRole.SUPER_ADMIN;
  };

  // Get available tenants for selection (only for Super Admin)
  const getAvailableTenants = () => {
    return tenants.map((tenant) => ({
      value: tenant.id,
      label: tenant.name,
    }));
  };

  // Check if farm selection is required for the selected role
  const isFarmSelectionRequired = (role: UserRole): boolean => {
    return role === UserRole.FARMER_ADMIN || role === UserRole.SUPERVISOR || role === UserRole.EMPLOYEE;
  };

  // Check if tenant selection is required for the selected role
  const isTenantSelectionRequired = (role: UserRole): boolean => {
    return role === UserRole.TENANT_ADMIN || role === UserRole.FARMER_ADMIN || role === UserRole.SUPERVISOR || role === UserRole.EMPLOYEE;
  };

  // Get available farms for the selected tenant (or current user's tenant if not super admin)
  const getAvailableFarms = () => {
    const selectedTenantId = canSelectTenant() ? formData.tenantId : currentUser?.tenantId;
    if (!selectedTenantId) return [];

    return farms
      .filter((farm) => farm.tenantId === selectedTenantId)
      .map((farm) => ({
        value: farm.id,
        label: farm.name,
      }));
  };

  return (
    <Box sx={{width: "100%"}}>
      <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3}}>
        <Typography variant="h5" component="h1">
          User Management
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
          disabled={
            currentUser?.role !== UserRole.SUPER_ADMIN && currentUser?.role !== UserRole.TENANT_ADMIN && currentUser?.role !== UserRole.FARMER_ADMIN
          }>
          Add User
        </Button>
      </Box>

      {/* Filters Section */}
      <Card sx={{mb: 2}}>
        <CardContent>
          <Box sx={{display: "flex", alignItems: "center", mb: 2}}>
            <FilterListIcon sx={{mr: 1}} />
            <Typography variant="h6" component="h2">
              Filters
            </Typography>
            {hasActiveFilters() && (
              <Button startIcon={<ClearIcon />} onClick={clearFilters} size="small" sx={{ml: "auto"}}>
                Clear All
              </Button>
            )}
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Search by Name"
                value={filters.name}
                onChange={(e) => handleFilterChange("name", e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Search by Email"
                value={filters.email}
                onChange={(e) => handleFilterChange("email", e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField fullWidth select label="Role" value={filters.role} onChange={(e) => handleFilterChange("role", e.target.value)} size="small">
                <MenuItem value="">All Roles</MenuItem>
                {Object.entries(roleLabels).map(([role, label]) => (
                  <MenuItem key={role} value={role}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                select
                label="Status"
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
                size="small">
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                select
                label="Tenant"
                value={filters.tenantId}
                onChange={(e) => handleFilterChange("tenantId", e.target.value)}
                size="small">
                <MenuItem value="">All Tenants</MenuItem>
                {tenants.map((tenant) => (
                  <MenuItem key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Paper sx={{width: "100%", mb: 2, overflow: "hidden"}}>
        {loading ? (
          <Box sx={{display: "flex", justifyContent: "center", p: 4}}>
            <CircularProgress />
          </Box>
        ) : filteredUsers.length === 0 ? (
          <Box sx={{p: 4, textAlign: "center"}}>
            <Typography variant="body1" color="textSecondary">
              {hasActiveFilters() ? "No users match the current filters." : 'No users found. Click "Add User" to create a new user.'}
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer sx={{maxHeight: 600}}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Tenant</TableCell>
                    <TableCell>Farm</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={roleLabels[user.role] || user.role}
                          size="small"
                          color={
                            user.role === UserRole.SUPER_ADMIN
                              ? "primary"
                              : user.role === UserRole.TENANT_ADMIN
                              ? "secondary"
                              : user.role === UserRole.FARMER_ADMIN
                              ? "info"
                              : "default"
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={user.isActive}
                              onChange={() => handleToggleStatus(user)}
                              color="primary"
                              disabled={!canEditUser(user) || user.role === UserRole.SUPER_ADMIN}
                            />
                          }
                          label={user.isActive ? "Active" : "Inactive"}
                        />
                      </TableCell>
                      <TableCell>{formatDate(user.createdAt)}</TableCell>
                      <TableCell>{getTenantName(user.tenantId)}</TableCell>
                      <TableCell>{getFarmName(user.farmId)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="View">
                          <IconButton size="small" onClick={() => {}}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <span>
                            <IconButton size="small" onClick={() => handleOpenEditDialog(user)} disabled={!canEditUser(user)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <span>
                            <IconButton size="small" color="error" onClick={() => handleOpenDeleteDialog(user)} disabled={!canDeleteUser(user)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredUsers.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>

      {/* Create User Dialog */}
      <Dialog open={openCreateDialog} onClose={handleCloseDialogs} maxWidth="sm" fullWidth>
        <DialogTitle>{t("Add New User")}</DialogTitle>
        <DialogContent>
          <Box sx={{mt: 2}}>
            <TextField
              fullWidth
              label="Full Name"
              name="name"
              value={formData.name || ""}
              onChange={handleInputChange}
              error={!!errors.name}
              helperText={errors.name}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email || ""}
              onChange={handleInputChange}
              error={!!errors.email}
              helperText={errors.email}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Password"
              name="password"
              type="password"
              value={formData.password || ""}
              onChange={handleInputChange}
              error={!!errors.password}
              helperText={errors.password || "Default: Password@123"}
              margin="normal"
              required
            />
            <TextField fullWidth select label="Role" name="role" value={formData.role || ""} onChange={handleInputChange} margin="normal" required>
              {getUserRoleOptions().map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            {/* Tenant Selection - Only show for Super Admin when creating non-Super Admin users */}
            {canSelectTenant() && formData.role !== UserRole.SUPER_ADMIN ? (
              <TextField
                fullWidth
                select
                label="Tenant"
                name="tenantId"
                value={formData.tenantId || ""}
                onChange={handleInputChange}
                error={!!errors.tenantId}
                helperText={errors.tenantId || "Select the tenant for this user"}
                margin="normal"
                required>
                {getAvailableTenants().map((tenant) => (
                  <MenuItem key={tenant.value} value={tenant.value}>
                    {tenant.label}
                  </MenuItem>
                ))}
              </TextField>
            ) : formData.role === UserRole.SUPER_ADMIN ? (
              /* Show info for Super Admin users */
              <TextField
                fullWidth
                label="Tenant"
                value="System Level Access"
                margin="normal"
                disabled
                helperText="Super Admin users have system-wide access and don't require tenant assignment"
              />
            ) : (
              /* Show current tenant info for non-Super Admin users */
              <TextField
                fullWidth
                label="Tenant"
                value={getTenantName(currentUser?.tenantId || "")}
                margin="normal"
                disabled
                helperText="User will be created in your current tenant"
              />
            )}

            {/* Farm Selection - Show for farm-related roles */}
            {formData.role && isFarmSelectionRequired(formData.role) && (
              <TextField
                fullWidth
                select
                label="Farm"
                name="farmId"
                value={formData.farmId || ""}
                onChange={handleInputChange}
                error={!!errors.farmId}
                helperText={errors.farmId || "Select the farm for this user"}
                margin="normal"
                required>
                {getAvailableFarms().map((farm) => (
                  <MenuItem key={farm.value} value={farm.value}>
                    {farm.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleCreateUser} color="primary" variant="contained">
            Create User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={openEditDialog} onClose={handleCloseDialogs} maxWidth="sm" fullWidth>
        <DialogTitle>{t("Edit User")}</DialogTitle>
        <DialogContent>
          <Box sx={{mt: 2}}>
            <TextField
              fullWidth
              label="Full Name"
              name="name"
              value={formData.name || ""}
              onChange={handleInputChange}
              error={!!errors.name}
              helperText={errors.name}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email || ""}
              onChange={handleInputChange}
              error={!!errors.email}
              helperText={errors.email}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="New Password (leave blank to keep current)"
              name="password"
              type="password"
              value={formData.password || ""}
              onChange={handleInputChange}
              error={!!errors.password}
              helperText={errors.password || "Leave blank to keep current password"}
              margin="normal"
            />
            <TextField
              fullWidth
              select
              label="Role"
              name="role"
              value={formData.role || ""}
              onChange={handleInputChange}
              margin="normal"
              required
              disabled={selectedUser?.role === UserRole.SUPER_ADMIN && currentUser?.id !== selectedUser?.id}>
              {getUserRoleOptions().map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  disabled={
                    // Prevent demoting yourself from SUPER_ADMIN
                    selectedUser?.id === currentUser?.id && option.value !== UserRole.SUPER_ADMIN && currentUser?.role === UserRole.SUPER_ADMIN
                  }>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            {/* Farm Selection - Show for farm-related roles */}
            {formData.role && isFarmSelectionRequired(formData.role) && (
              <TextField
                fullWidth
                select
                label="Farm"
                name="farmId"
                value={formData.farmId || ""}
                onChange={handleInputChange}
                error={!!errors.farmId}
                helperText={errors.farmId || "Select the farm for this user"}
                margin="normal"
                required>
                {getAvailableFarms().map((farm) => (
                  <MenuItem key={farm.value} value={farm.value}>
                    {farm.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleUpdateUser} color="primary" variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDialogs} maxWidth="sm" fullWidth>
        <DialogTitle>{t("Delete User")}</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to delete the user "{selectedUser?.name}"? This action cannot be undone.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained">
            Delete User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{vertical: "top", horizontal: "right"}}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{width: "100%"}}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserListPage;
